import * as THREE from "three";
import { config } from "../config.js";
import { glowTexture } from "./pods.js";
import { statsAt } from "../sim/upgrades.js";
import { fieldOf } from "../sim/scale.js";
import { floorYOf, markScale } from "./scale-view.js";

// 派手な演出(見た目だけ)。試合の状態(world)には入れず、world.events を見て描く。
// だから、ここをどれだけ派手にしても、リプレイ・R.W.N.D.の重さや結果は変わらない。
// 量は描画レベル(particleScale)で減らす。

const VERT = `
attribute float size;
attribute vec4 tint;
uniform float pxScale;
uniform float fovScale; // 視野角が広いほど小さく(ほかの物と同じ縮み方にする。faceCamera)
uniform float gain; // 粒の明るさ(後半ほど下げる。config.graphics.glare)
varying vec4 vTint;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  // カメラのすぐ近くの粒は大きく写りすぎて画面を覆うので、上限を付け、近いほど薄くする
  float near = clamp((-mv.z - 8.0) / 40.0, 0.0, 1.0);
  vTint = vec4(tint.rgb * gain, tint.a * near);
  gl_PointSize = min(size * fovScale * (300.0 / -mv.z), 90.0) * pxScale;
  gl_Position = projectionMatrix * mv;
}`;
const FRAG = `
uniform sampler2D map;
varying vec4 vTint;
void main() {
  vec4 t = texture2D(map, gl_PointCoord);
  gl_FragColor = vec4(vTint.rgb * t.rgb, t.a * vTint.a);
}`;

// 色の決まり(読みやすさ。仕様書「色と形の決まり」):
// 自分の攻撃は青〜紫の寒色で細長い形、敵の弾は赤い玉+輪、経験値は金色のひし形、修理カプセルは緑の十字。
// 赤・橙は「危ない物」と「敵が壊れた爆発」だけに使う
const C = config.graphics.colors;

export function createEffects(scene) {
  const max = config.graphics.maxParticles;
  let particleScale = 1;

  // --- 粒(火花・破片・光) ---
  const pos = new Float32Array(max * 3);
  const tint = new Float32Array(max * 4);
  const size = new Float32Array(max);
  const vel = new Float32Array(max * 3);
  const life = new Float32Array(max);
  const maxLife = new Float32Array(max);
  const baseSize = new Float32Array(max);
  const baseColor = new Float32Array(max * 3);
  const drag = new Float32Array(max);
  let head = 0;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
  geo.setAttribute("tint", new THREE.BufferAttribute(tint, 4).setUsage(THREE.DynamicDrawUsage));
  geo.setAttribute("size", new THREE.BufferAttribute(size, 1).setUsage(THREE.DynamicDrawUsage));
  const points = new THREE.Points(
    geo,
    new THREE.ShaderMaterial({ uniforms: { map: { value: glowTexture() }, pxScale: { value: 1 }, fovScale: { value: 1 }, gain: { value: 1 } }, vertexShader: VERT, fragmentShader: FRAG, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true })
  );
  points.frustumCulled = false;
  scene.add(points);

  function emit(x, y, z, vx, vy, vz, lifeS, sz, color, dragK = 1.5) {
    const i = head;
    head = (head + 1) % max;
    pos.set([x, y, z], i * 3);
    vel.set([vx, vy, vz], i * 3);
    life[i] = maxLife[i] = lifeS;
    baseSize[i] = sz;
    baseColor.set([color.r, color.g, color.b], i * 3);
    drag[i] = dragK;
  }

  const tmpColor = new THREE.Color();
  function burst(x, y, z, n, speed, lifeS, sz, hex, dragK) {
    const c = tmpColor.set(hex);
    const count = Math.max(1, Math.round(n * particleScale));
    for (let i = 0; i < count; i++) {
      // 球の上の向き(見た目だけなので Math.random でよい)
      const u = Math.random() * 2 - 1;
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(1 - u * u);
      const s = speed * (0.3 + Math.random() * 0.7);
      emit(x, y, z, r * Math.cos(a) * s, r * Math.sin(a) * s, u * s, lifeS * (0.5 + Math.random() * 0.5), sz * (0.6 + Math.random() * 0.8), c, dragK);
    }
  }

  // --- 衝撃波の輪 ---
  const rings = [];
  const ringGeo = new THREE.RingGeometry(0.85, 1, 48);
  for (let i = 0; i < 24; i++) {
    const m = new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ color: 0xffc070, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, side: THREE.DoubleSide }));
    m.visible = false;
    scene.add(m);
    rings.push({ mesh: m, t: 0, dur: 1, r: 1 });
  }
  let ringHead = 0;
  function ring(x, y, z, radius, dur, hex) {
    const r = rings[ringHead];
    ringHead = (ringHead + 1) % rings.length;
    r.mesh.position.set(x, y, z);
    r.mesh.material.color.set(hex);
    r.mesh.visible = true;
    r.t = 0;
    r.dur = dur;
    r.r = radius;
  }

  // --- 弾(まとめ描き) ---
  function boltMesh(maxN, geometry, hex) {
    const m = new THREE.InstancedMesh(geometry, new THREE.MeshBasicMaterial({ color: hex, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false }), maxN);
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.frustumCulled = false;
    m.count = 0;
    scene.add(m);
    return m;
  }
  const boltGeo = new THREE.CylinderGeometry(0.45, 0.45, 9, 6, 1, false).rotateX(Math.PI / 2);
  const boltGlowGeo = new THREE.CylinderGeometry(1.3, 1.3, 11, 8, 1, false).rotateX(Math.PI / 2);
  const shots = {
    beam: [boltMesh(600, boltGeo, 0xffffff), boltMesh(600, boltGlowGeo, 0x2f8cff)],
    spread: [boltMesh(600, boltGeo, 0xffffff), boltMesh(600, boltGlowGeo, C.spread)],
    missile: [boltMesh(200, new THREE.ConeGeometry(0.9, 4.5, 6).rotateX(Math.PI / 2), 0xffffff), boltMesh(200, new THREE.CylinderGeometry(1.8, 1.8, 6, 6).rotateX(Math.PI / 2), C.missile)],
  };
  // 敵の弾: 丸い玉と、まわりの輪(輪はカメラの方へ向ける)。自分の弾(細長い)と形でも分かれるように
  const enemyShotMeshes = [boltMesh(800, new THREE.SphereGeometry(1.1, 8, 6), 0xfff0f0), boltMesh(800, new THREE.SphereGeometry(2.4, 10, 8), C.enemyShot)];
  const enemyShotRing = boltMesh(800, new THREE.RingGeometry(3.2, 3.8, 20), C.enemyShotRing);
  enemyShotRing.material.side = THREE.DoubleSide;
  enemyShotRing.material.opacity = 0.7;
  const camQ = new THREE.Quaternion(); // 前のフレームのカメラの向き(輪をカメラへ向ける)
  const gemMesh = new THREE.InstancedMesh(new THREE.OctahedronGeometry(1.4), new THREE.MeshStandardMaterial({ color: C.xp, emissive: C.xpGlow, emissiveIntensity: 1.4, metalness: 0.3, roughness: 0.3 }), 1500);
  gemMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  gemMesh.frustumCulled = false;
  gemMesh.count = 0;
  scene.add(gemMesh);

  // 修理カプセル: 緑の十字(カメラの方を向けたまま、回さない)と、まわりで回る光の枠
  const repairMat = new THREE.MeshStandardMaterial({ color: C.repair, emissive: C.repairGlow, emissiveIntensity: 1.4, metalness: 0.3, roughness: 0.3 });
  const repairMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(5.4, 1.8, 1.8), repairMat, 40);
  const repairMesh2 = new THREE.InstancedMesh(new THREE.BoxGeometry(1.8, 5.4, 1.8), repairMat, 40);
  const repairGlow = new THREE.InstancedMesh(new THREE.OctahedronGeometry(5.2), new THREE.MeshBasicMaterial({ color: C.repair, blending: THREE.AdditiveBlending, transparent: true, opacity: 0.35, depthWrite: false, wireframe: true }), 40);
  for (const m of [repairMesh, repairMesh2, repairGlow]) {
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.frustumCulled = false;
    m.count = 0;
    scene.add(m);
  }

  // --- サーベルと主砲 ---
  const saberMat = new THREE.MeshBasicMaterial({ color: C.saber, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false });
  const saberCoreMat = new THREE.MeshBasicMaterial({ color: 0xffffff, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false });
  const sabers = [];
  const maxSabers = 8; // 進化したサーベルの本数まで(src/sim/upgrades.js)
  for (let i = 0; i < maxSabers; i++) {
    const g = new THREE.Group();
    g.add(new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.8, 12), saberMat));
    g.add(new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.6, 12), saberCoreMat));
    g.visible = false;
    scene.add(g);
    sabers.push(g);
  }
  const mainGun = new THREE.Group();
  const gunLen = 900;
  const gunOuter = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, gunLen, 24, 1, true).rotateX(Math.PI / 2).translate(0, 0, -gunLen / 2), new THREE.MeshBasicMaterial({ color: 0x4fb4ff, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, side: THREE.DoubleSide }));
  const gunCore = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, gunLen, 16, 1, true).rotateX(Math.PI / 2).translate(0, 0, -gunLen / 2), new THREE.MeshBasicMaterial({ color: 0xffffff, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false }));
  mainGun.add(gunOuter, gunCore);
  mainGun.visible = false;
  scene.add(mainGun);
  let mainGunRadius = 8;

  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const v = new THREE.Vector3();
  const sc = new THREE.Vector3(1, 1, 1);
  const fwd = new THREE.Vector3(0, 0, 1);
  function placeAlong(mesh, i, x, y, z, vx, vy, vz, s = 1) {
    v.set(vx, vy, vz).normalize();
    q.setFromUnitVectors(fwd, v);
    sc.setScalar(s);
    m4.compose(v.set(x, y, z), q, sc);
    mesh.setMatrixAt(i, m4);
  }

  // 1ステップ分の出来事から演出を出す
  function onEvents(events, world) {
    const s = world.scale; // スケール感: 敵の爆発は、敵と同じだけ大きくする(自機まわりの演出はそのまま)
    for (const e of events) {
      if (e.k === "kill") {
        if (e.big) {
          const b = e.size || s; // 大きな敵の大きさ(母艦・小母艦・大玉)
          burst(e.x, e.y, e.z, 400, 140 * b, 2.2, 14 * b, 0xffb040, 0.8);
          burst(e.x, e.y, e.z, 200, 60 * b, 2.8, 30 * b, 0xff4020, 0.6);
          burst(e.x, e.y, e.z, 60, 10 * b, 1.2, 90 * b, 0xffffff, 0.5);
          for (let i = 0; i < 5; i++) ring(e.x, e.y, e.z, (80 + i * 40) * b, 1.2 + i * 0.25, i % 2 ? 0xff6030 : 0xffd080);
        } else {
          const gunner = e.type === "gunner";
          burst(e.x, e.y, e.z, gunner ? 70 : 45, (gunner ? 80 : 60) * s, 1.0, 6 * s, 0xffa040); // 爆発は暖色(敵が壊れた印)
          burst(e.x, e.y, e.z, 10, 20 * s, 0.5, 22 * s, 0xffffff);
          burst(e.x, e.y, e.z, 14, 30 * s, 1.4, 3 * s, 0xff5020, 0.4);
          ring(e.x, e.y, e.z, (gunner ? 26 : 16) * s, 0.5, 0xffc070);
        }
      } else if (e.k === "fire" && e.w === "spread") {
        ring(e.x, e.y, -8, 10, 0.35, C.spread); // 撃った瞬間の波紋
      } else if (e.k === "hit") {
        burst(e.x, e.y, e.z, 6, 40, 0.25, 3, 0x9fd8ff);
      } else if (e.k === "blast") {
        const col = e.mine ? 0xff4a20 : C.missile; // 機雷の爆発は敵の色(危ない物)
        burst(e.x, e.y, e.z, e.mine ? 60 : 30, e.r * 6, 0.6, 7 * (e.mine ? s : 1), col);
        ring(e.x, e.y, e.z, e.r * 2.2, 0.4, col);
      } else if (e.k === "flak") {
        const floorY = floorYOf(world);
        burst(e.x, floorY, 0, 40, 60 * s, 0.6, 8 * s, 0xff6030);
      } else if (e.k === "saberHit") {
        burst(e.x, e.y, e.z, 14, 70, 0.35, 3, C.saber);
      } else if (e.k === "mainGun") {
        mainGunRadius = e.r;
        burst(e.x, e.y, -6, 30, 60, 0.5, 8, 0x8fd0ff);
      } else if (e.k === "enemyFire" && e.boss) {
        const b = e.size;
        burst(e.x, e.y, e.mz, 20, 30 * b, 0.4, 16 * b, 0xff6a3a); // 主砲の砲口から(mz: 弾の出る所)
      } else if (e.k === "heal") {
        const p = world.player;
        burst(p.x, p.y, 0, 50, 35, 0.8, 5, C.repair, 1);
        ring(p.x, p.y, 0, 14, 0.6, C.repair);
      } else if (e.k === "hurt") {
        const p = world.player;
        burst(p.x, p.y, 0, 30, 50, 0.5, 4, 0xffffff);
        ring(p.x, p.y, 0, 12, 0.4, C.enemyShot);
      }
    }
  }

  // 毎フレーム: world の今の状態から弾・経験値・サーベル・主砲を置き、粒を進める
  function update(world, dtReal, timeScale) {
    // 後半(倍率が大きいほど)は粒を少し暗くする(2026-10-30 オーナーの感想「後半やや光が強すぎてまぶしい」)
    points.material.uniforms.gain.value = Math.pow(world.scale || 1, -config.graphics.glare.particleFade);
    const counts = { beam: 0, spread: 0, missile: 0 };
    for (const s of world.shots) {
      const pair = shots[s.kind];
      const i = counts[s.kind]++;
      if (i >= pair[0].instanceMatrix.count) continue;
      for (const m of pair) placeAlong(m, i, s.x, s.y, s.z, s.vx, s.vy, s.vz, s.size || 1);
      if (s.kind === "missile" && Math.random() < 0.6 * particleScale) emit(s.x, s.y, s.z, 0, 0, 0, 0.5, 3, tmpColor.set(C.missileTrail), 0);
    }
    for (const k of Object.keys(shots)) for (const m of shots[k]) {
      m.count = Math.min(counts[k], m.instanceMatrix.count);
      m.instanceMatrix.needsUpdate = true;
    }
    let n = 0;
    const flick = 1 + Math.sin(performance.now() / 60) * 0.15;
    const mark = markScale(world); // 敵の弾と経験値は、引いた画面でも小さくなりすぎないように少し大きく見せる(当たりは変わらない)
    for (const s of world.enemyShots) {
      if (n >= 800) break;
      for (const m of enemyShotMeshes) placeAlong(m, n, s.x, s.y, s.z, s.vx, s.vy, s.vz, flick * mark);
      sc.setScalar((2.2 - flick) * mark);
      m4.compose(v.set(s.x, s.y, s.z), camQ, sc);
      enemyShotRing.setMatrixAt(n, m4);
      n++;
    }
    for (const m of [...enemyShotMeshes, enemyShotRing]) {
      m.count = n;
      m.instanceMatrix.needsUpdate = true;
    }
    n = 0;
    const spin = performance.now() / 400;
    for (const g of world.gems) {
      if (n >= 1500) break;
      q.setFromAxisAngle(v.set(0, 1, 0), spin + n);
      sc.setScalar((g.v > 1 ? 1.6 : 1.1) * mark);
      m4.compose(v.set(g.x, g.y, g.z), q, sc);
      gemMesh.setMatrixAt(n++, m4);
    }
    gemMesh.count = n;
    gemMesh.instanceMatrix.needsUpdate = true;

    // 修理カプセル(緑の箱と光)
    n = 0;
    for (const c of world.repairs) {
      if (n >= 40) break;
      sc.setScalar((1 + Math.sin(performance.now() / 150) * 0.12) * mark);
      m4.compose(v.set(c.x, c.y, c.z), camQ, sc);
      repairMesh.setMatrixAt(n, m4);
      repairMesh2.setMatrixAt(n, m4);
      q.setFromAxisAngle(v.set(0.3, 1, 0.2).normalize(), spin * 1.5 + n);
      m4.compose(v.set(c.x, c.y, c.z), q, sc);
      repairGlow.setMatrixAt(n, m4);
      n++;
    }
    repairMesh.count = repairMesh2.count = repairGlow.count = n;
    for (const m of [repairMesh, repairMesh2, repairGlow]) m.instanceMatrix.needsUpdate = true;

    // サーベル(world の角度と同じ置き方。sim の stepSaber と合わせる)
    const p = world.player;
    const saber = statsAt("saber", p.weapons.saber || 0);
    const sCount = saber ? saber.count : 0;
    const sRadius = saber ? saber.radius : 0;
    for (let i = 0; i < sabers.length; i++) {
      const g = sabers[i];
      g.visible = i < sCount;
      if (!g.visible) continue;
      const a = p.saberAngle + (i / sCount) * Math.PI * 2;
      g.position.set(p.x + Math.cos(a) * sRadius, p.y, Math.sin(a) * sRadius);
      g.rotation.set(0, Math.PI / 2 - a, 0); // 刃を外向きに
    }

    mainGun.visible = p.mainGunFiring > 0;
    if (mainGun.visible) {
      const k = Math.min(1, p.mainGunFiring / 0.45);
      mainGun.position.set(p.x, p.y, -5);
      gunOuter.scale.set(mainGunRadius * (0.6 + k * 0.5), mainGunRadius * (0.6 + k * 0.5), 1);
      gunCore.scale.set(mainGunRadius * 0.9, mainGunRadius * 0.9, 1);
      gunOuter.material.opacity = 0.55 * k;
      gunCore.material.opacity = k;
    }

    // 粒を進める(SLOW中は粒もゆっくり)
    const dt = dtReal * timeScale;
    const flow = fieldOf(world).scrollSpeed * 0.35;
    for (let i = 0; i < max; i++) {
      if (life[i] <= 0) {
        tint[i * 4 + 3] = 0;
        size[i] = 0;
        continue;
      }
      life[i] -= dt;
      const d = Math.exp(-drag[i] * dt);
      vel[i * 3] *= d;
      vel[i * 3 + 1] *= d;
      vel[i * 3 + 2] *= d;
      pos[i * 3] += vel[i * 3] * dt;
      pos[i * 3 + 1] += vel[i * 3 + 1] * dt;
      pos[i * 3 + 2] += (vel[i * 3 + 2] + flow) * dt;
      const k = Math.max(0, life[i] / maxLife[i]);
      tint[i * 4] = baseColor[i * 3];
      tint[i * 4 + 1] = baseColor[i * 3 + 1] * (0.4 + 0.6 * k);
      tint[i * 4 + 2] = baseColor[i * 3 + 2] * k;
      tint[i * 4 + 3] = k;
      size[i] = baseSize[i] * (0.5 + 0.5 * k);
    }
    geo.attributes.position.needsUpdate = true;
    geo.attributes.tint.needsUpdate = true;
    geo.attributes.size.needsUpdate = true;

    for (const r of rings) {
      if (!r.mesh.visible) continue;
      r.t += dt;
      const k = r.t / r.dur;
      if (k >= 1) {
        r.mesh.visible = false;
        continue;
      }
      const s = r.r * (0.15 + 0.85 * Math.sqrt(k));
      r.mesh.scale.setScalar(s);
      r.mesh.material.opacity = (1 - k) * 0.8;
    }
  }

  // リプレイの位置を変えたとき(古い粒は消す)
  function clear() {
    life.fill(0);
    for (const r of rings) r.mesh.visible = false;
  }

  return {
    onEvents,
    update,
    clear,
    setParticleScale(s) {
      particleScale = s;
    },
    setPixelRatio(pr) {
      points.material.uniforms.pxScale.value = pr;
    },
    faceCamera(camera) {
      camQ.copy(camera.quaternion);
      points.material.uniforms.fovScale.value = Math.tan(THREE.MathUtils.degToRad(31)) / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)); // 視野角62°のとき1
      for (const r of rings) if (r.mesh.visible) r.mesh.quaternion.copy(camera.quaternion);
    },
  };
}
